export type ChatHistoryMessage = { role: string; content: string | unknown };

const TRUNCATION_SUFFIX = "\n...[truncated for context]";

/** ~30 turn pairs (user + assistant) */
export const DEFAULT_MAX_MESSAGES = 60;
export const DEFAULT_MAX_CHARS_PER_MESSAGE = 10_000;
export const DEFAULT_MAX_TOTAL_PAYLOAD_CHARS = 200_000;

export type TruncationStats = {
  originalCount: number;
  sentCount: number;
  messagesDropped: number;
  messagesTruncated: number;
  totalChars: number;
  payloadChars: number;
};

export function getMessageContentLength(content: string | unknown): number {
  if (typeof content === "string") return content.length;
  if (content == null) return 0;
  try {
    return JSON.stringify(content).length;
  } catch {
    return String(content).length;
  }
}

/** Coerce unknown values to string for safe .startsWith / .includes / .replace use. */
export function asString(value: unknown): string {
  if (typeof value === "string") return value;
  if (value == null) return "";
  if (typeof value === "number" || typeof value === "boolean" || typeof value === "bigint") {
    return String(value);
  }
  try {
    return String(value);
  } catch {
    return "";
  }
}

/** Coerce stored/API message content to plain text for UI estimates and rendering. */
export function messageContentToText(content: string | unknown): string {
  if (typeof content === "string") return content;
  if (content == null) return "";
  if (typeof content === "number" || typeof content === "boolean" || typeof content === "bigint") {
    return String(content);
  }
  try {
    const serialized = JSON.stringify(content);
    return typeof serialized === "string" ? serialized : String(content);
  } catch {
    return String(content);
  }
}

export function countMessageContentLines(content: string | unknown): number {
  const text = messageContentToText(content);
  if (!text) return 1;
  return text.split("\n").length;
}

function truncateText(text: string, maxChars: number): { text: string; truncated: boolean } {
  if (text.length <= maxChars) return { text, truncated: false };
  const budget = Math.max(0, maxChars - TRUNCATION_SUFFIX.length);
  return { text: text.slice(0, budget) + TRUNCATION_SUFFIX, truncated: true };
}

function truncateMessageContent(
  content: string | unknown,
  maxChars: number
): { content: string | unknown; truncated: boolean } {
  if (typeof content === "string") {
    const { text, truncated } = truncateText(content, maxChars);
    return { content: text, truncated };
  }

  if (Array.isArray(content)) {
    let truncated = false;
    const next = content.map((part) => {
      if (part && typeof part === "object" && "type" in part && (part as { type?: string }).type === "text") {
        const textPart = part as { type: string; text?: string };
        if (typeof textPart.text === "string") {
          const { text, truncated: partTruncated } = truncateText(textPart.text, maxChars);
          if (partTruncated) truncated = true;
          return { ...textPart, text };
        }
      }
      return part;
    });
    return { content: next, truncated };
  }

  return { content, truncated: false };
}

function measurePayloadChars(messages: ChatHistoryMessage[]): number {
  try {
    return JSON.stringify(messages).length;
  } catch {
    return messages.reduce((sum, m) => sum + getMessageContentLength(m.content), 0);
  }
}

export function limitChatHistoryForApi(
  messages: ChatHistoryMessage[],
  options?: {
    maxMessages?: number;
    maxCharsPerMessage?: number;
    maxTotalPayloadChars?: number;
  }
): { messages: ChatHistoryMessage[]; stats: TruncationStats } {
  const maxMessages = options?.maxMessages ?? DEFAULT_MAX_MESSAGES;
  const maxCharsPerMessage = options?.maxCharsPerMessage ?? DEFAULT_MAX_CHARS_PER_MESSAGE;
  const maxTotalPayloadChars = options?.maxTotalPayloadChars ?? DEFAULT_MAX_TOTAL_PAYLOAD_CHARS;

  const originalCount = messages.length;
  const systemMessages = messages.filter((m) => m.role === "system");
  let conversation = messages.filter((m) => m.role !== "system");

  let messagesDropped = Math.max(0, conversation.length - maxMessages);
  if (conversation.length > maxMessages) {
    conversation = conversation.slice(-maxMessages);
  }

  let messagesTruncated = 0;
  conversation = conversation.map((m) => {
    const { content, truncated } = truncateMessageContent(m.content, maxCharsPerMessage);
    if (truncated) messagesTruncated++;
    return { ...m, content };
  });

  let result: ChatHistoryMessage[] = [...systemMessages, ...conversation];
  let payloadChars = measurePayloadChars(result);

  while (payloadChars > maxTotalPayloadChars && conversation.length > 1) {
    conversation.shift();
    messagesDropped++;
    result = [...systemMessages, ...conversation];
    payloadChars = measurePayloadChars(result);
  }

  const totalChars = result.reduce((sum, m) => sum + getMessageContentLength(m.content), 0);

  return {
    messages: result,
    stats: {
      originalCount,
      sentCount: result.length,
      messagesDropped,
      messagesTruncated,
      totalChars,
      payloadChars,
    },
  };
}

export function logOutboundChatPayload(
  label: string,
  messages: ChatHistoryMessage[],
  extra?: Record<string, unknown>
): void {
  if (!import.meta.env.DEV) return;

  const roleCounts = messages.reduce<Record<string, number>>((acc, m) => {
    acc[m.role] = (acc[m.role] ?? 0) + 1;
    return acc;
  }, {});

  const systemMsg = messages.find((m) => m.role === "system");
  const nonSystem = messages.filter((m) => m.role !== "system");
  const first = nonSystem[0];
  const last = nonSystem[nonSystem.length - 1];

  console.log(`[${label}] Outbound chat payload`, {
    messageCount: messages.length,
    roleCounts,
    systemPromptChars: systemMsg ? getMessageContentLength(systemMsg.content) : 0,
    payloadChars: measurePayloadChars(messages),
    firstMessage: first
      ? { role: first.role, contentLength: getMessageContentLength(first.content) }
      : null,
    lastMessage: last
      ? { role: last.role, contentLength: getMessageContentLength(last.content) }
      : null,
    ...extra,
  });
}
